import {
  buildMainFieldFilterParams,
  buildMainFieldParamsForRowSelection,
} from '../domain/dataKi/searchParams';
import {
  MAIN_FIELD_TYPE,
  MainFieldType,
  Option,
  QueryParameter,
} from '../domain/schemas';

/**
 * 台別データを表示するために必要なパラメータを作成
 *
 * 台別データを表示するために、検索時に詳細条件で指定した「機種,SISタイプ,メーカー」が利用されるため、パラメータとしてそれぞれ「kiList,sisTypes,makers」を付与する必要がある。
 * 仕様：https://www.notion.so/ForSijiritu-1c0a4e41249f81638081c138cb17cd4e
 */
export const dataKiDaiSearchConditionParams = <
  T extends {
    mainField?: MainFieldType;
    kiList?: string[];
    sisTypes?: string[];
    makers?: string[];
    makerGroups?: string[];
  }
>(
  mainField: QueryParameter,
  dataKi1stRow: QueryParameter | undefined,
  searchCondition: T,
  makerOptions?: Option[] | undefined
) => {
  // 詳細条件で設定された機種やSISタイプ、メーカーの支持率計算のために必要なパラメータ
  const searchConditionForTypes = {
    makers: searchCondition.makers,
    makerGroups: searchCondition.makerGroups,
    sisTypes: searchCondition.sisTypes,
  };

  const selectedRowMainFieldType = buildMainFieldParamsForRowSelection(
    mainField,
    searchCondition,
    makerOptions
  );

  // 機種行を選択したときの選択されたタイプ(makers[])やメーカー(sisTypes[])を把握
  // 出力がタイプ・メーカーの場合に、どのタイプやメーカーから選択された機種行なのかを判別するため
  if (dataKi1stRow) {
    const narrowedByFirstRowMakerOrGroup =
      dataKi1stRow.name === 'makerGroups'
        ? buildMainFieldFilterParams(
            {
              name: 'makerGroups',
              value: dataKi1stRow.value,
            },
            searchCondition,
            makerOptions
          )
        : dataKi1stRow.name === 'makers'
        ? buildMainFieldFilterParams(
            {
              name: 'makers',
              value: dataKi1stRow.value,
            },
            searchCondition,
            makerOptions
          )
        : undefined;

    return {
      ...searchConditionForTypes,
      ...selectedRowMainFieldType,
      ...(narrowedByFirstRowMakerOrGroup ?? {}),
      [dataKi1stRow.name]: [dataKi1stRow.value],
    };
  }

  // 出力がタイプ・メーカーで、機種行ではない箇所で選択された場合
  if (searchCondition.mainField !== MAIN_FIELD_TYPE.KI_TUSHO_MEI) {
    // selectedRowMainFieldType から得られる kiList（3行目で選択された機種など）
    const kiListFromSelected = (
      selectedRowMainFieldType as Partial<Record<string, string[]>>
    ).kiList;

    // finalKiList を優先順で決定:
    // 1) selectedRowMainFieldType の kiList（ユーザが行で選択した値）
    // 2) 詳細条件の searchCondition.kiList
    // 3) mainField が 'kiList' のときのみ mainField.value を単一要素で使う
    // 4) それ以外は undefined（APIに送らない）
    const finalKiList: string[] | undefined = (() => {
      if (kiListFromSelected !== undefined && kiListFromSelected.length > 0) {
        return kiListFromSelected;
      }
      if (
        searchCondition.kiList !== undefined &&
        searchCondition.kiList.length > 0
      ) {
        return searchCondition.kiList;
      }
      // mainField.value を使うのは mainField が 'kiList' の場合のみ（メーカーコード等の誤挿入防止）
      if (
        mainField.name === 'kiList' &&
        mainField.value !== null &&
        mainField.value !== undefined
      ) {
        return [String(mainField.value)];
      }
      return searchCondition.kiList;
    })();

    return {
      ...searchConditionForTypes,
      ...selectedRowMainFieldType,
      kiList: finalKiList,
    };
  }

  return {
    ...searchConditionForTypes,
    ...selectedRowMainFieldType,
    kiList: [mainField.value],
  };
};
